import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Centered empty-state block: optional icon, heading, body, optional
 * CTA. Used by Start-Page (Batch 7) for the "Noch keine eigenen
 * Geschichten" state and available for other empty-state surfaces
 * across future batches (Library empty-filter, Notifications empty,
 * Account payment-history empty, etc.).
 *
 * Body copy renders at opacity-50 per the Sönke visual convention for
 * secondary-text on white surfaces. Pass a brand-voiced body string —
 * no generic "Keine Einträge" placeholders.
 */
export interface EmptyStateBlockProps {
  /** Optional icon/illustration above the heading — Lucide component
   *  or `<img>`. Centered with a ~46px size unless you override. */
  icon?: ReactNode;
  heading: string;
  body: string;
  /**
   * Optional CTA. `icon` is rendered left of the label (e.g. a
   * lucide `<Plus />` for "Add"-style empty-state buttons). Wired in
   * the Batch 8 Library bug-fix commit (2026-04-26) so callers can
   * keep the plus-icon affordance after the leading `+ ` literal was
   * stripped from the STRINGS copy (Doppel-Plus fix).
   */
  cta?: { label: string; onClick: () => void; icon?: ReactNode };
  className?: string;
  testId?: string;
}

export function EmptyStateBlock({
  icon,
  heading,
  body,
  cta,
  className,
  testId,
}: EmptyStateBlockProps) {
  return (
    <div
      className={cn('flex w-full flex-col items-center gap-3 pt-3.5 text-center', className)}
      data-testid={testId}
    >
      {icon && <div className="flex items-center justify-center">{icon}</div>}
      <p className="text-sm font-semibold leading-[14px] text-dream-navy">{heading}</p>
      <p className="max-w-[240px] text-xs leading-4 text-dream-navy opacity-50">{body}</p>
      {cta && (
        <button
          type="button"
          onClick={cta.onClick}
          className="mt-1 flex items-center justify-center gap-1.5 rounded-lg bg-dream-gold px-4 py-[14px] text-xs font-semibold text-dream-navy transition-colors hover:bg-dream-gold/90"
        >
          {cta.icon}
          {cta.label}
        </button>
      )}
    </div>
  );
}
